import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, Edit2, Trash2, Loader2, Save, X, Gift, Users, Building2,
  DollarSign, Copy, Share2, TrendingUp, Check
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const AdminReferrals = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('client');
  const [showForm, setShowForm] = useState(false);
  
  // Client-to-Client Referral Program
  const [clientProgram, setClientProgram] = useState({
    enabled: true,
    referrerReward: 50,
    referrerRewardType: 'credit', // credit, percentage, fixed
    refereeReward: 25,
    refereeRewardType: 'credit',
    minimumPurchase: 100,
    expirationDays: 90,
    maxReferrals: 0, // 0 = unlimited
    description: 'Refer a friend and you both save! You get $50 credit, they get $25 off their first visit.'
  });

  // Spa-to-Spa Referral Program (B2B)
  const [b2bProgram, setB2bProgram] = useState({
    enabled: true,
    referrerReward: 100,
    referrerRewardType: 'credit', // credit, percentage, subscription_discount
    minimumMonths: 3,
    description: 'Refer another spa to MdSpa360 and earn $100 platform credit for each referral that signs up!'
  });

  // Mock referral stats
  const clientStats = {
    totalReferrals: 47,
    pendingReferrals: 8,
    completedReferrals: 39,
    totalRewardsGiven: 2450,
    topReferrer: 'Sarah M.',
    topReferrerCount: 7
  };

  const b2bStats = {
    totalReferrals: 3,
    pendingReferrals: 1,
    completedReferrals: 2,
    totalCreditsEarned: 200
  };

  // Mock referral list
  const [clientReferrals] = useState([
    { id: 1, referrer: 'Sarah M.', referee: 'Emily K.', date: '2026-02-20', status: 'completed', reward: 50 },
    { id: 2, referrer: 'John D.', referee: 'Mike R.', date: '2026-02-18', status: 'completed', reward: 50 },
    { id: 3, referrer: 'Lisa P.', referee: 'Anna S.', date: '2026-02-25', status: 'pending', reward: 50 },
    { id: 4, referrer: 'Sarah M.', referee: 'Chris L.', date: '2026-02-22', status: 'completed', reward: 50 },
    { id: 5, referrer: 'Rachel G.', referee: 'David W.', date: '2026-02-24', status: 'pending', reward: 50 },
  ]);

  const [b2bReferrals] = useState([
    { id: 1, referrer: 'Ecobel Med Spa', referee: 'Glow Aesthetics', date: '2026-01-15', status: 'completed', reward: 100 },
    { id: 2, referrer: 'Ecobel Med Spa', referee: 'Beauty Haven Spa', date: '2026-02-10', status: 'completed', reward: 100 },
    { id: 3, referrer: 'Ecobel Med Spa', referee: 'Luxe Skin Clinic', date: '2026-02-20', status: 'pending', reward: 100 },
  ]);

  const handleSaveClientProgram = () => {
    toast({
      title: 'Client Referral Program Updated',
      description: 'Your referral program settings have been saved.'
    });
  };

  const handleSaveB2BProgram = () => {
    toast({
      title: 'B2B Referral Program Updated',
      description: 'Your spa referral program settings have been saved.'
    });
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText('https://app.mdspa360.com/refer/ecobel');
    toast({
      title: 'Link Copied!',
      description: 'Referral link copied to clipboard.'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Referral Programs</h1>
        <p className="text-gray-600 mt-2">Create and manage referral programs for clients and partner spas</p>
      </div>

      <Tabs defaultValue="client" onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="client" className="gap-2">
            <Users className="h-4 w-4" />
            Client Referrals
          </TabsTrigger>
          <TabsTrigger value="b2b" className="gap-2">
            <Building2 className="h-4 w-4" />
            Spa Referrals
          </TabsTrigger>
        </TabsList>

        {/* Client-to-Client Tab */}
        <TabsContent value="client" className="space-y-6">
          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Referrals</p>
                    <p className="text-2xl font-bold">{clientStats.totalReferrals}</p>
                  </div>
                  <Gift className="h-8 w-8 text-primary opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="text-2xl font-bold">{clientStats.completedReferrals}</p>
                  </div>
                  <Check className="h-8 w-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Rewards Given</p>
                    <p className="text-2xl font-bold">${clientStats.totalRewardsGiven}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Top Referrer</p>
                    <p className="text-2xl font-bold">{clientStats.topReferrerCount}</p>
                    <p className="text-xs text-gray-500">{clientStats.topReferrer}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-purple-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Program Settings */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>Client Referral Program Settings</CardTitle>
                  <CardDescription>Configure how clients can refer friends and family</CardDescription>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={clientProgram.enabled}
                    onChange={(e) => setClientProgram({...clientProgram, enabled: e.target.checked})}
                    className="rounded"
                  />
                  <span className="text-sm font-medium">Enabled</span>
                </label>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2">
                    <Gift className="h-4 w-4 text-green-600" />
                    Referrer Reward (Who Refers)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Reward Amount</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="number"
                          value={clientProgram.referrerReward}
                          onChange={(e) => setClientProgram({...clientProgram, referrerReward: parseFloat(e.target.value)})}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Reward Type</Label>
                      <select
                        value={clientProgram.referrerRewardType}
                        onChange={(e) => setClientProgram({...clientProgram, referrerRewardType: e.target.value})}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="credit">Account Credit</option>
                        <option value="percentage">% Off Next Visit</option>
                        <option value="fixed">Fixed Discount</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-medium flex items-center gap-2">
                    <Users className="h-4 w-4 text-blue-600" />
                    Referee Reward (New Client)
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Reward Amount</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <Input
                          type="number"
                          value={clientProgram.refereeReward}
                          onChange={(e) => setClientProgram({...clientProgram, refereeReward: parseFloat(e.target.value)})}
                          className="pl-10"
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Reward Type</Label>
                      <select
                        value={clientProgram.refereeRewardType}
                        onChange={(e) => setClientProgram({...clientProgram, refereeRewardType: e.target.value})}
                        className="w-full mt-1 px-3 py-2 border rounded-lg"
                      >
                        <option value="credit">Account Credit</option>
                        <option value="percentage">% Off First Visit</option>
                        <option value="fixed">Fixed Discount</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Minimum Purchase ($)</Label>
                  <Input
                    type="number"
                    value={clientProgram.minimumPurchase}
                    onChange={(e) => setClientProgram({...clientProgram, minimumPurchase: parseFloat(e.target.value)})}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum spend to qualify</p>
                </div>
                <div>
                  <Label>Reward Expiration (days)</Label>
                  <Input
                    type="number"
                    value={clientProgram.expirationDays}
                    onChange={(e) => setClientProgram({...clientProgram, expirationDays: parseInt(e.target.value)})}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">0 = never expires</p>
                </div>
                <div>
                  <Label>Max Referrals per Client</Label>
                  <Input
                    type="number"
                    value={clientProgram.maxReferrals}
                    onChange={(e) => setClientProgram({...clientProgram, maxReferrals: parseInt(e.target.value)})}
                    className="mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">0 = unlimited</p>
                </div>
              </div>

              <div>
                <Label>Program Description (shown to clients)</Label>
                <Textarea
                  value={clientProgram.description}
                  onChange={(e) => setClientProgram({...clientProgram, description: e.target.value})}
                  rows={2}
                  className="mt-1"
                />
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveClientProgram}>
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Recent Referrals */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Client Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {clientReferrals.map((ref) => (
                  <div key={ref.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                        <Gift className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-medium">{ref.referrer} → {ref.referee}</p>
                        <p className="text-sm text-gray-500">{new Date(ref.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-medium text-green-600">${ref.reward}</span>
                      <Badge variant={ref.status === 'completed' ? 'default' : 'secondary'}>
                        {ref.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* B2B Spa-to-Spa Tab */}
        <TabsContent value="b2b" className="space-y-6">
          {/* Stats */}
          <div className="grid md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Spa Referrals</p>
                    <p className="text-2xl font-bold">{b2bStats.totalReferrals}</p>
                  </div>
                  <Building2 className="h-8 w-8 text-primary opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Active Partners</p>
                    <p className="text-2xl font-bold">{b2bStats.completedReferrals}</p>
                  </div>
                  <Check className="h-8 w-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Credits Earned</p>
                    <p className="text-2xl font-bold">${b2bStats.totalCreditsEarned}</p>
                  </div>
                  <DollarSign className="h-8 w-8 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-2xl font-bold">{b2bStats.pendingReferrals}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-yellow-500 opacity-50" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Your Referral Link */}
          <Card className="bg-gradient-to-r from-primary/10 to-primary/5">
            <CardHeader>
              <CardTitle>Your Spa Referral Link</CardTitle>
              <CardDescription>Share this link with other spa owners to earn rewards</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input 
                  value="https://app.mdspa360.com/refer/ecobel" 
                  readOnly 
                  className="bg-white font-mono"
                />
                <Button onClick={copyReferralLink} variant="outline">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy
                </Button>
                <Button>
                  <Share2 className="h-4 w-4 mr-2" />
                  Share
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* B2B Program Settings */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle>B2B Referral Program</CardTitle>
                  <CardDescription>Earn rewards by referring other spas to MdSpa360</CardDescription>
                </div>
                <Badge className="bg-green-100 text-green-800">Active</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-purple-50 rounded-lg">
                <h4 className="font-medium mb-2">How it works:</h4>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                    Share your unique referral link with another spa owner
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                    They sign up for MdSpa360 and stay active for {b2bProgram.minimumMonths} months
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-6 h-6 bg-purple-200 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                    You earn ${b2bProgram.referrerReward} in platform credits!
                  </li>
                </ul>
              </div>

              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <p className="font-medium">Your Reward per Referral</p>
                  <p className="text-2xl font-bold text-green-600">${b2bProgram.referrerReward}</p>
                </div>
                <Gift className="h-12 w-12 text-primary opacity-30" />
              </div>
            </CardContent>
          </Card>

          {/* B2B Referral History */}
          <Card>
            <CardHeader>
              <CardTitle>Your Spa Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="divide-y">
                {b2bReferrals.map((ref) => (
                  <div key={ref.id} className="py-3 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">{ref.referee}</p>
                        <p className="text-sm text-gray-500">Referred on {new Date(ref.date).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-medium text-green-600">${ref.reward}</span>
                      <Badge variant={ref.status === 'completed' ? 'default' : 'secondary'}>
                        {ref.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminReferrals;
